import React from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Lock, Eye, Database, UserCheck, Globe, Mail, Clock, AlertCircle } from 'lucide-react';

const PrivacyPage = () => {
  const { language } = useLanguage();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-oar-blue via-oar-blue-dark to-oar-blue">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Politique de Confidentialité
          </h1>
          <p className="text-white/80">
            Conforme au RGPD - Dernière mise à jour : Février 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-oar-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
            
            {/* Introduction */}
            <div className="bg-oar-blue-soft rounded-lg p-6">
              <p className="text-oar-text leading-relaxed">
                L'Organisation Africaine de la Russophonie (OAR) s'engage à protéger votre vie privée et vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations conformément au Règlement Général sur la Protection des Données (RGPD) de l'Union Européenne et aux législations applicables.
              </p>
            </div>

            {/* Responsable du traitement */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <UserCheck className="w-6 h-6" />
                1. Responsable du Traitement
              </h2>
              <div className="bg-oar-bg rounded-lg p-6">
                <p className="text-oar-text"><strong>Organisation Africaine de la Russophonie (OAR)</strong></p>
                <p className="text-oar-text">Siège social : Bangui, République Centrafricaine</p>
                <p className="text-oar-text">Email : contact@russophonie-afrique.com</p>
                <p className="text-oar-text mt-4">
                  <strong>Délégué à la Protection des Données (DPO) :</strong><br />
                  Pour toute question relative à la protection de vos données, contactez : dpo@russophonie-afrique.com
                </p>
              </div>
            </div>

            {/* Données collectées */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Database className="w-6 h-6" />
                2. Données Personnelles Collectées
              </h2>
              <p className="text-oar-text leading-relaxed mb-4">
                Nous collectons différents types de données selon les services que vous utilisez :
              </p>
              
              <h3 className="font-semibold text-oar-text mt-6 mb-3">2.1 Lors de l'adhésion à l'Association</h3>
              <ul className="list-disc list-inside text-oar-text space-y-1 ml-4">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Pays de résidence</li>
                <li>Profession/Occupation</li>
                <li>Niveau d'études</li>
                <li>Domaine d'expertise</li>
              </ul>

              <h3 className="font-semibold text-oar-text mt-6 mb-3">2.2 Lors de la candidature au Programme Étudiants</h3>
              <ul className="list-disc list-inside text-oar-text space-y-1 ml-4">
                <li>Informations d'identité (nom, prénom, date de naissance)</li>
                <li>Coordonnées (email, téléphone)</li>
                <li>Parcours académique</li>
                <li>Niveau de langue russe</li>
                <li>Motivations</li>
              </ul>

              <h3 className="font-semibold text-oar-text mt-6 mb-3">2.3 Lors de l'inscription à la newsletter</h3>
              <ul className="list-disc list-inside text-oar-text space-y-1 ml-4">
                <li>Adresse email</li>
                <li>Préférences de langue</li>
              </ul>

              <h3 className="font-semibold text-oar-text mt-6 mb-3">2.4 Données de navigation (collectées automatiquement)</h3>
              <ul className="list-disc list-inside text-oar-text space-y-1 ml-4">
                <li>Adresse IP</li>
                <li>Type de navigateur et système d'exploitation</li>
                <li>Pages visitées et durée de visite</li>
                <li>Pays et ville (géolocalisation approximative via IP)</li>
                <li>Type d'appareil (mobile, ordinateur, tablette)</li>
              </ul>
            </div>

            {/* Finalités */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6" />
                3. Finalités du Traitement
              </h2>
              <p className="text-oar-text leading-relaxed mb-4">
                Vos données personnelles sont collectées et traitées pour les finalités suivantes :
              </p>
              <table className="w-full border-collapse mt-4">
                <thead>
                  <tr className="bg-oar-bg">
                    <th className="border border-oar-border p-3 text-left text-sm font-semibold text-oar-text">Finalité</th>
                    <th className="border border-oar-border p-3 text-left text-sm font-semibold text-oar-text">Base légale (RGPD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Gestion des adhésions</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Exécution du contrat</td>
                  </tr>
                  <tr className="bg-oar-bg/50">
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Traitement des candidatures étudiantes</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Consentement</td>
                  </tr>
                  <tr>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Envoi de newsletters et communications</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Consentement</td>
                  </tr>
                  <tr className="bg-oar-bg/50">
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Réponse aux demandes de contact</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Intérêt légitime</td>
                  </tr>
                  <tr>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Statistiques et amélioration du site</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Intérêt légitime</td>
                  </tr>
                  <tr className="bg-oar-bg/50">
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Traitement des paiements</td>
                    <td className="border border-oar-border p-3 text-sm text-oar-text">Exécution du contrat</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Durée de conservation */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6" />
                4. Durée de Conservation des Données
              </h2>
              <p className="text-oar-text leading-relaxed mb-4">
                Nous conservons vos données personnelles uniquement pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 ml-4">
                <li><strong>Données d'adhésion :</strong> Durée de l'adhésion + 3 ans après la fin de l'adhésion</li>
                <li><strong>Données de candidature :</strong> 2 ans après la décision finale</li>
                <li><strong>Données de newsletter :</strong> Jusqu'au désabonnement + 1 an</li>
                <li><strong>Messages de contact :</strong> 3 ans après le dernier échange</li>
                <li><strong>Données de navigation :</strong> 13 mois maximum</li>
                <li><strong>Données de paiement :</strong> 10 ans (obligations légales comptables)</li>
              </ul>
            </div>

            {/* Destinataires */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Globe className="w-6 h-6" />
                5. Destinataires des Données
              </h2>
              <p className="text-oar-text leading-relaxed mb-4">
                Vos données personnelles peuvent être partagées avec :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 ml-4">
                <li><strong>Personnel autorisé de l'OAR :</strong> Pour la gestion des adhésions et programmes</li>
                <li><strong>Prestataires techniques :</strong> Hébergement, paiement en ligne (Paystack), envoi d'emails</li>
                <li><strong>Partenaires institutionnels :</strong> Universités russes (uniquement pour les candidats au programme étudiants, avec leur consentement)</li>
              </ul>
              <p className="text-oar-text leading-relaxed mt-4">
                <strong>Nous ne vendons jamais vos données personnelles à des tiers.</strong>
              </p>
            </div>

            {/* Transferts internationaux */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                6. Transferts Internationaux de Données
              </h2>
              <p className="text-oar-text leading-relaxed">
                Certaines de vos données peuvent être transférées vers des pays situés en dehors de l'Espace Économique Européen (EEE), notamment :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-4 ml-4">
                <li>République Centrafricaine (siège de l'OAR)</li>
                <li>Fédération de Russie (pour les candidatures au programme étudiants)</li>
              </ul>
              <p className="text-oar-text leading-relaxed mt-4">
                Dans ce cas, nous nous assurons que des garanties appropriées sont mises en place pour protéger vos données (clauses contractuelles types, etc.).
              </p>
            </div>

            {/* Droits */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6" />
                7. Vos Droits (RGPD)
              </h2>
              <p className="text-oar-text leading-relaxed mb-4">
                Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit d'accès</h4>
                  <p className="text-sm text-oar-text">Obtenir une copie de vos données personnelles</p>
                </div>
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit de rectification</h4>
                  <p className="text-sm text-oar-text">Corriger des données inexactes ou incomplètes</p>
                </div>
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit à l'effacement</h4>
                  <p className="text-sm text-oar-text">Demander la suppression de vos données ("droit à l'oubli")</p>
                </div>
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit à la limitation</h4>
                  <p className="text-sm text-oar-text">Restreindre le traitement de vos données</p>
                </div>
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit à la portabilité</h4>
                  <p className="text-sm text-oar-text">Recevoir vos données dans un format structuré</p>
                </div>
                <div className="bg-oar-bg rounded-lg p-4">
                  <h4 className="font-semibold text-oar-blue mb-2">Droit d'opposition</h4>
                  <p className="text-sm text-oar-text">Vous opposer au traitement de vos données</p>
                </div>
              </div>
              <p className="text-oar-text leading-relaxed mt-6">
                Pour exercer ces droits, envoyez un email à <strong>dpo@russophonie-afrique.com</strong> avec une copie de votre pièce d'identité. Nous répondrons dans un délai d'un mois.
              </p>
            </div>

            {/* Sécurité */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                8. Sécurité des Données
              </h2>
              <p className="text-oar-text leading-relaxed">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction, notamment :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-4 ml-4">
                <li>Chiffrement des données sensibles (mots de passe hashés avec BCrypt)</li>
                <li>Connexions sécurisées (HTTPS/TLS)</li>
                <li>Accès restreint aux données (authentification JWT)</li>
                <li>Sauvegardes régulières</li>
                <li>Surveillance et journalisation des accès</li>
              </ul>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                9. Cookies et Technologies Similaires
              </h2>
              <p className="text-oar-text leading-relaxed">
                Notre site utilise des cookies et technologies similaires pour :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-4 ml-4">
                <li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site (session, authentification)</li>
                <li><strong>Cookies analytiques :</strong> Mesure d'audience et amélioration du site</li>
                <li><strong>Cookies de préférences :</strong> Mémorisation de vos choix (langue, etc.)</li>
              </ul>
              <p className="text-oar-text leading-relaxed mt-4">
                Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.
              </p>
            </div>

            {/* Réclamation */}
            <div className="bg-oar-orange/10 border border-oar-orange/30 rounded-lg p-6">
              <h2 className="font-heading text-xl font-bold text-oar-orange mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                10. Droit de Réclamation
              </h2>
              <p className="text-oar-text leading-relaxed">
                Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès d'une autorité de contrôle, notamment celle de votre pays de résidence habituelle, de votre lieu de travail ou du lieu où la violation aurait été commise.
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                En France : Commission Nationale de l'Informatique et des Libertés (CNIL) - www.cnil.fr
              </p>
            </div>

            {/* Contact */}
            <div className="bg-oar-blue-soft rounded-lg p-6 mt-8">
              <h2 className="font-heading text-xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact
              </h2>
              <p className="text-oar-text">
                Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits :
              </p>
              <p className="text-oar-text mt-4">
                <strong>Délégué à la Protection des Données :</strong> dpo@russophonie-afrique.com
              </p>
              <p className="text-oar-text">
                <strong>Contact général :</strong> contact@russophonie-afrique.com
              </p>
              <p className="text-oar-text mt-4">
                <strong>Adresse postale :</strong><br />
                Organisation Africaine de la Russophonie<br />
                Bangui, République Centrafricaine
              </p>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPage;
